import React, { useEffect, useState } from 'react'
import { Card } from 'antd';
import Radio from '@mui/material/Radio';
import Form from 'react-bootstrap/Form';
import Button from '@mui/material/Button';
import { GetPriceDefaultCoupon, AddFreeCouponAPI, GetCountriesDiscountCoupons, SaveDiscountDouponsAPI } from "../../../../api";
import getSymbolFromCurrency from 'currency-symbol-map'
import InputGroup from 'react-bootstrap/InputGroup';
import ErrorAlert from "../../../../commonFunctions/Alerts/ErrorAlert";
import moment from 'moment';
import { DatePicker } from 'antd';
import FormatNumbers from '../../../../commonFunctions/FormatNumbers';


// ================= Shared helpers =================

// Safely turn "1,299.99" / "34.87" / 34.87 into a number
const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return NaN;
  return parseFloat(String(v).replace(/,/g, ''));
};

// Format a value for display (2 decimals, or whole number for JPY)
const formatMoney = (v, allowDecimals = true) => {
  const n = toNumber(v);
  if (isNaN(n)) return "";
  if (!allowDecimals) {
    // JPY: whole number only, e.g. 12,500 (never 12,500.00)
    return Math.round(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  return FormatNumbers(n.toFixed(2));
};

// Empty -> 0 for the API payload
const orZero = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = toNumber(v);
  return isNaN(n) ? 0 : v;
};

// One handler for every discounted-price field.
// allowDecimals = true  -> 2.77, 34.87 (max 2 decimals)
// allowDecimals = false -> whole numbers only (Japan)
const makePriceHandler = (listPrice, setValue, setPercent, setAmount, allowDecimals = true) => (e) => {
  const value = e.target.value.replace(/,/g, '');

  if (value === "") {
    setValue("");
    setPercent(0);
    setAmount(0);
    return;
  }

  const lp = toNumber(listPrice);
  if (isNaN(lp) || lp <= 0) return;

  const regex = allowDecimals ? /^\d*\.?\d{0,2}$/ : /^\d*$/;
  if (!regex.test(value)) return;

  const entered = parseFloat(value);

  // Discounted price cannot be higher than the list price
  if (!isNaN(lp) && !isNaN(entered) && entered > lp) return;

  setValue(value);

  if (!isNaN(entered) && lp > 0) {
    const amount = lp - entered;
    setAmount(allowDecimals ? amount.toFixed(2) : String(Math.round(amount)));
    setPercent(((amount / lp) * 100).toFixed(2));
  }
};

const COUPON_CODE_ERROR = "Please enter a valid coupon code. It must be between 6-20 characters and may include only UPPERCASE letters (A-Z), numbers (0-9), periods (.), dashes (-), and underscores (_).";
const COUPON_CODE_REGEX = /^[A-Z0-9._-]{6,20}$/;


const AddCoupon = ({ code }) => {

  const [selectedValue, setSelectedValue] = useState('free-coupon');
  const [loading_btn, setloading_btn] = useState(false)

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };


  // ================= FREE COUPON =================
  const currentDate = moment().startOf('day');
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(moment().add(7, 'days'));
  const [couponCodeFree, setcouponCodeFree] = useState('');

  useEffect(() => {
    if (startDate) {
      setEndDate(moment(startDate).add(7, 'days'));
    }
  }, [startDate]);

  const handleStartDateChange = (date) => {
    if (date && date.isSameOrAfter(currentDate, 'day')) {
      setStartDate(date);
    }
  };

  const handleFreeCouponCreate = (e) => {
    e.preventDefault();

    const isValid = COUPON_CODE_REGEX.test(couponCodeFree);

    const SD = moment(new Date(startDate)).format('YYYY-MM-DD HH:mm:ss');
    const ED = moment(new Date(endDate)).format('YYYY-MM-DD HH:mm:ss');

    if (isValid || couponCodeFree === "") {
      setloading_btn(true);
      AddFreeCouponAPI(code, SD, ED, couponCodeFree, setloading_btn);
    } else {
      ErrorAlert("Error", COUPON_CODE_ERROR);
      setloading_btn(false)
    }
  };


  // ================= DISCOUNT COUPON DATES =================
  const currentDateDiscount = moment().format('DD-MM-YYYY');
  const [startDateDiscount, setStartDateDiscount] = useState(currentDateDiscount);
  const [endDateDiscount, setEndDateDiscount] = useState("");
  const [couponCodeDiscount, setcouponCodeDiscount] = useState("");

  const calculateEndDateDiscount = (start) => {
    return moment(start, 'DD-MM-YYYY').clone().add(30, 'days').format('DD-MM-YYYY');
  };

  const handleStartDateChangeDiscount = (date) => {
    if (date && date.isSameOrAfter(currentDate, 'day')) {
      const newStartDate = date.format('DD-MM-YYYY');
      setStartDateDiscount(newStartDate);
      setEndDateDiscount(calculateEndDateDiscount(newStartDate));
    }
  };

  useEffect(() => {
    setEndDateDiscount(calculateEndDateDiscount(startDateDiscount));
  }, [startDateDiscount]);


  // ================= GLOBAL PRICE STATE =================
  const [countriesData, setcountriesData] = useState([])

  const [DGlobalPricing, setDGlobalPricing] = useState("")
  const [DTip, setDTip] = useState("")
  const [DDiscountValue, setDDiscountValue] = useState("")
  const [DDiscountPercent, setDDiscountPercent] = useState("")
  const [DDiscountAmount, setDDiscountAmount] = useState("")
  const [MinDefaultValue, setMinDefaultValue] = useState("")
  const [PriceRangeMinDefault, setPriceRangeMinDefault] = useState("")
  const [PriceRangeMaxDefault, setPriceRangeMaxDefault] = useState("")


  // ================= COUNTRY STATE =================
  // USA
  const [USAListPrice, setUSAListPrice] = useState("")
  const [USADiscountValue, setUSADiscountValue] = useState("")
  const [USADiscountPercent, setUSADiscountPercent] = useState("")
  const [USADiscountAmount, setUSADiscountAmount] = useState("")
  const [USATip, setUSATip] = useState("")
  const [USAMinValue, setUSAMinValue] = useState("")

  // Australia
  const [AusListPrice, setAusListPrice] = useState("")
  const [AusDiscountValue, setAusDiscountValue] = useState("")
  const [AusDiscountPercent, setAusDiscountPercent] = useState("")
  const [AusDiscountAmount, setAusDiscountAmount] = useState("")
  const [AusTip, setAusTip] = useState("")
  const [AusminValue, setAusminValue] = useState("")

  // Brazil
  const [BrazilListPrice, setBrazilListPrice] = useState("")
  const [BrazilDiscountValue, setBrazilDiscountValue] = useState("")
  const [BrazilDiscountPercent, setBrazilDiscountPercent] = useState("")
  const [BrazilDiscountAmount, setBrazilDiscountAmount] = useState("")
  const [BrazilTip, setBrazilTip] = useState("")
  const [BrazilminValue, setBrazilminValue] = useState("")

  // Canada
  const [CanadaListPrice, setCanadaListPrice] = useState("")
  const [CanadaDiscountValue, setCanadaDiscountValue] = useState("")
  const [CanadaDiscountPercent, setCanadaDiscountPercent] = useState("")
  const [CanadaDiscountAmount, setCanadaDiscountAmount] = useState("")
  const [CanadaTip, setCanadaTip] = useState("")
  const [CanadaminValue, setCanadaminValue] = useState("")

  // Chile
  const [ChileListPrice, setChileListPrice] = useState("")
  const [ChileDiscountValue, setChileDiscountValue] = useState("")
  const [ChileDiscountPercent, setChileDiscountPercent] = useState("")
  const [ChileDiscountAmount, setChileDiscountAmount] = useState("")
  const [ChileTip, setChileTip] = useState("")
  const [ChileminValue, setChileminValue] = useState("")

  // Colombia
  const [ColumbiaListPrice, setColumbiaListPrice] = useState("")
  const [ColumbiaDiscountValue, setColumbiaDiscountValue] = useState("")
  const [ColumbiaDiscountPercent, setColumbiaDiscountPercent] = useState("")
  const [ColumbiaDiscountAmount, setColumbiaDiscountAmount] = useState("")
  const [ColumbiaTip, setColumbiaTip] = useState("")
  const [ColumbiaminValue, setColumbiaminValue] = useState("")

  // Egypt
  const [EgyptListPrice, setEgyptListPrice] = useState("")
  const [EgyptDiscountValue, setEgyptDiscountValue] = useState("")
  const [EgyptDiscountPercent, setEgyptDiscountPercent] = useState("")
  const [EgyptDiscountAmount, setEgyptDiscountAmount] = useState("")
  const [EgyptTip, setEgyptTip] = useState("")
  const [EgyptminValue, setEgyptminValue] = useState("")

  // European Union
  const [EUListPrice, setEUListPrice] = useState("")
  const [EUDiscountValue, setEUDiscountValue] = useState("")
  const [EUDiscountPercent, setEUDiscountPercent] = useState("")
  const [EUDiscountAmount, setEUDiscountAmount] = useState("")
  const [EUTip, setEUTip] = useState("")
  const [EUminValue, setEUminValue] = useState("")

  // United Kingdom
  const [GBPListPrice, setGBPListPrice] = useState("")
  const [GBPDiscountValue, setGBPDiscountValue] = useState("")
  const [GBPDiscountPercent, setGBPDiscountPercent] = useState("")
  const [GBPDiscountAmount, setGBPDiscountAmount] = useState("")
  const [GBPTip, setGBPTip] = useState("")
  const [GBPminValue, setGBPminValue] = useState("")

  // Indonesia
  const [IndonesiaListPrice, setIndonesiaListPrice] = useState("")
  const [IndonesiaDiscountValue, setIndonesiaDiscountValue] = useState("")
  const [IndonesiaDiscountPercent, setIndonesiaDiscountPercent] = useState("")
  const [IndonesiaDiscountAmount, setIndonesiaDiscountAmount] = useState("")
  const [IndonesiaTip, setIndonesiaTip] = useState("")
  const [IndonesiaminValue, setIndonesiaminValue] = useState("")

  // Israel
  const [IsrealListPrice, setIsrealListPrice] = useState("")
  const [IsrealDiscountValue, setIsrealDiscountValue] = useState("")
  const [IsrealDiscountPercent, setIsrealDiscountPercent] = useState("")
  const [IsrealDiscountAmount, setIsrealDiscountAmount] = useState("")
  const [IsrealTip, setIsrealTip] = useState("")
  const [IsrealminValue, setIsrealminValue] = useState("")

  // India
  const [IndiaListPrice, setIndiaListPrice] = useState("")
  const [IndiaDiscountValue, setIndiaDiscountValue] = useState("")
  const [IndiaDiscountPercent, setIndiaDiscountPercent] = useState("")
  const [IndiaDiscountAmount, setIndiaDiscountAmount] = useState("")
  const [IndiaTip, setIndiaTip] = useState("")
  const [IndiaminValue, setIndiaminValue] = useState("")

  // Japan
  const [JapanListPrice, setJapanListPrice] = useState("")
  const [JapanDiscountValue, setJapanDiscountValue] = useState("")
  const [JapanDiscountPercent, setJapanDiscountPercent] = useState("")
  const [JapanDiscountAmount, setJapanDiscountAmount] = useState("")
  const [JapanTip, setJapanTip] = useState("")
  const [JapanminValue, setJapanminValue] = useState("")

  // South Korea
  const [SKListPrice, setSKListPrice] = useState("")
  const [SKDiscountValue, setSKDiscountValue] = useState("")
  const [SKDiscountPercent, setSKDiscountPercent] = useState("")
  const [SKDiscountAmount, setSKDiscountAmount] = useState("")
  const [SKTip, setSKTip] = useState("")
  const [SKminValue, setSKminValue] = useState("")

  // Mexico
  const [MexicoListPrice, setMexicoListPrice] = useState("")
  const [MexicoDiscountValue, setMexicoDiscountValue] = useState("")
  const [MexicoDicountPercent, setMexicoDicountPercent] = useState("")
  const [MexicoDisountAmount, setMexicoDisountAmount] = useState("")
  const [MexicoTip, setMexicoTip] = useState("")
  const [MexicominValue, setMexicominValue] = useState("")

  // Malaysia
  const [MalaysiaListPrice, setMalaysiaListPrice] = useState("")
  const [MalaysiaDiscountValue, setMalaysiaDiscountValue] = useState("")
  const [MalaysiaDiscountPercent, setMalaysiaDiscountPercent] = useState("")
  const [MalaysiaDiscountAmount, setMalaysiaDiscountAmount] = useState("")
  const [MalaysiaTip, setMalaysiaTip] = useState("")
  const [MalaysiaminValue, setMalaysiaminValue] = useState("")

  // Nigeria
  const [NigeriaListPrice, setNigeriaListPrice] = useState("")
  const [NigeriaDiscountValue, setNigeriaDiscountValue] = useState("")
  const [NigeriaDiscountPercent, setNigeriaDiscountPercent] = useState("")
  const [NigeriaDiscountAmount, setNigeriaDiscountAmount] = useState("")
  const [NigeriaTip, setNigeriaTip] = useState("")
  const [NigeriaminValue, setNigeriaminValue] = useState("")

  // Norway
  const [NorwayListPrice, setNorwayListPrice] = useState("")
  const [NorwayDiscountValue, setNorwayDiscountValue] = useState("")
  const [NorwayDiscountPercent, setNorwayDiscountPercent] = useState("")
  const [NorwayDiscountAmount, setNorwayDiscountAmount] = useState("")
  const [NorwayTip, setNorwayTip] = useState("")
  const [NorwayminValue, setNorwayminValue] = useState("")

  // Peru
  const [PeruListPrice, setPeruListPrice] = useState("")
  const [PeruDiscountValue, setPeruDiscountValue] = useState("")
  const [PeruDiscountPercent, setPeruDiscountPercent] = useState("")
  const [PeruDiscountAmount, setPeruDiscountAmount] = useState("")
  const [PeruTip, setPeruTip] = useState("")
  const [Peruminvalue, setPeruminvalue] = useState("")

  // Philippines
  const [PhilipinesListPrice, setPhilipinesListPrice] = useState("")
  const [PhiliphinesDiscountValue, setPhiliphinesDiscountValue] = useState("")
  const [PhilipinesDiscountPercent, setPhilipinesDiscountPercent] = useState("")
  const [PhilipinesAmount, setPhilipinesAmount] = useState("")
  const [PhilipinesTip, setPhilipinesTip] = useState("")
  const [PhilipinesminValue, setPhilipinesminValue] = useState("")

  // Poland
  const [PolandListPrice, setPolandListPrice] = useState("")
  const [PolandDiscountValue, setPolandDiscountValue] = useState("")
  const [PolandDiscountPercent, setPolandDiscountPercent] = useState("")
  const [PolandDiscountAmount, setPolandDiscountAmount] = useState("")
  const [PolandTip, setPolandTip] = useState("")
  const [PolandminValue, setPolandminValue] = useState("")

  // Romania
  const [RomaniaListPrice, setRomaniaListPrice] = useState("")
  const [RomaniaDiscountValue, setRomaniaDiscountValue] = useState("")
  const [RomaniaDiscountPercent, setRomaniaDiscountPercent] = useState("")
  const [RomaniaDiscountAmount, setRomaniaDiscountAmount] = useState("")
  const [RomaniaTip, setRomaniaTip] = useState("")
  const [Romaniaminvalue, setRomaniaminvalue] = useState("")

  // Russia
  const [RussiaListPrice, setRussiaListPrice] = useState("")
  const [RussiaDiscountValue, setRussiaDiscountValue] = useState("")
  const [RussiaDiscountPercent, setRussiaDiscountPercent] = useState("")
  const [RussiaDiscountAmount, setRussiaDiscountAmount] = useState("")
  const [RussiaTip, setRussiaTip] = useState("")
  const [RussiaminValue, setRussiaminValue] = useState("")

  // Singapore
  const [SingaporeListPrice, setSingaporeListPrice] = useState("")
  const [SingaporeDiscountValue, setSingaporeDiscountValue] = useState("")
  const [SingaporeDiscountPercent, setSingaporeDiscountPercent] = useState("")
  const [SingaporeDiscountAmount, setSingaporeDiscountAmount] = useState("")
  const [SingaporeTip, setSingaporeTip] = useState("")
  const [SingaporeminValue, setSingaporeminValue] = useState("")

  // Thailand
  const [ThailandListPrice, setThailandListPrice] = useState("")
  const [ThailandDiscountValue, setThailandDiscountValue] = useState("")
  const [ThailandDiscountPercent, setThailandDiscountPercent] = useState("")
  const [ThailandDiscountAmount, setThailandDiscountAmount] = useState("")
  const [ThailandTip, setThailandTip] = useState("")
  const [ThailandminValue, setThailandminValue] = useState("")

  // Turkey
  const [TurkeyListPrice, setTurkeyListPrice] = useState("")
  const [TurkeyDiscountValue, setTurkeyDiscountValue] = useState("")
  const [TurkeyDiscountPercent, setTurkeyDiscountPercent] = useState("")
  const [TurkeyDiscountAmount, setTurkeyDiscountAmount] = useState("")
  const [TurkeyTip, setTurkeyTip] = useState("")
  const [TurkeyminValue, setTurkeyminValue] = useState("")

  // Taiwan
  const [TaiwanListPrice, setTaiwanListPrice] = useState("")
  const [TaiwanDiscountValue, setTaiwanDiscountValue] = useState("")
  const [TaiwanDiscountPercent, setTaiwanDiscountPercent] = useState("")
  const [TaiwanDiscountAmount, setTaiwanDiscountAmount] = useState("")
  const [TaiwanTip, setTaiwanTip] = useState("")
  const [TaiwanminValue, setTaiwanminValue] = useState("")

  // Vietnam
  const [VietnamListPrice, setVietnamListPrice] = useState("")
  const [VietnamDiscountValue, setVietnamDiscountValue] = useState("")
  const [VietnamDiscountPercent, setVietnamDiscountPercent] = useState("")
  const [VietnamDisocuntAmount, setVietnamDisocuntAmount] = useState("")
  const [VietnamTip, setVietnamTip] = useState("")
  const [VietnamminValue, setVietnamminValue] = useState("")

  // South Africa
  const [SAListPrice, setSAListPrice] = useState("")
  const [SADiscountValue, setSADiscountValue] = useState("")
  const [SADiscountPercent, setSADiscountPercent] = useState("")
  const [SADiscountAmount, setSADiscountAmount] = useState("")
  const [SATip, setSATip] = useState("")
  const [SAminValue, setSAminValue] = useState("")


  // ================= LOAD DATA =================
  useEffect(() => {

    GetPriceDefaultCoupon(code, setDGlobalPricing, setPriceRangeMinDefault, setPriceRangeMaxDefault, setMinDefaultValue, setDTip)

    GetCountriesDiscountCoupons(code, setcountriesData,
      setUSATip, setUSAMinValue, setUSAListPrice,
      setAusTip, setAusminValue, setAusListPrice,
      setBrazilTip, setBrazilminValue, setBrazilListPrice,
      setCanadaTip, setCanadaminValue, setCanadaListPrice,
      setChileTip, setChileminValue, setChileListPrice,
      setColumbiaTip, setColumbiaminValue, setColumbiaListPrice,
      setEgyptTip, setEgyptminValue, setEgyptListPrice,
      setEUTip, setEUminValue, setEUListPrice,
      setGBPTip, setGBPminValue, setGBPListPrice,
      setIndonesiaTip, setIndonesiaminValue, setIndonesiaListPrice,
      setIsrealTip, setIsrealminValue, setIsrealListPrice,
      setIndiaTip, setIndiaminValue, setIndiaListPrice,
      setJapanTip, setJapanminValue, setJapanListPrice,
      setSKTip, setSKminValue, setSKListPrice,
      setMexicoTip, setMexicominValue, setMexicoListPrice,
      setMalaysiaTip, setMalaysiaminValue, setMalaysiaListPrice,
      setNigeriaTip, setNigeriaminValue, setNigeriaListPrice,
      setNorwayTip, setNorwayminValue, setNorwayListPrice,
      setPeruTip, setPeruminvalue, setPeruListPrice,
      setPhilipinesTip, setPhilipinesminValue, setPhilipinesListPrice,
      setPolandTip, setPolandminValue, setPolandListPrice,
      setRomaniaTip, setRomaniaminvalue, setRomaniaListPrice,
      setRussiaTip, setRussiaminValue, setRussiaListPrice,
      setSingaporeTip, setSingaporeminValue, setSingaporeListPrice,
      setThailandTip, setThailandminValue, setThailandListPrice,
      setTurkeyTip, setTurkeyminValue, setTurkeyListPrice,
      setTaiwanTip, setTaiwanminValue, setTaiwanListPrice,
      setVietnamTip, setVietnamminValue, setVietnamListPrice,
      setSATip, setSAminValue, setSAListPrice
    )

  }, [code])


  // ================= GLOBAL PRICE HANDLER =================
  const handleChangeGlobalPrice = makePriceHandler(DGlobalPricing, setDDiscountValue, setDDiscountPercent, setDDiscountAmount);


  // ================= COUNTRY CONFIG =================
  // `index` = position in countriesData (same order as the API / payload).
  // `decimals: false` = whole numbers only (Japan).
  // `inTable: false` = sent in payload but not shown in the table (Turkey).
  const countries = [
    { index: 0, name: "United States", currency: "USD", list: USAListPrice, value: USADiscountValue, percent: USADiscountPercent, amount: USADiscountAmount, tip: USATip, min: USAMinValue, setValue: setUSADiscountValue, setPercent: setUSADiscountPercent, setAmount: setUSADiscountAmount },
    { index: 1, name: "Australia", currency: "AUD", list: AusListPrice, value: AusDiscountValue, percent: AusDiscountPercent, amount: AusDiscountAmount, tip: AusTip, min: AusminValue, setValue: setAusDiscountValue, setPercent: setAusDiscountPercent, setAmount: setAusDiscountAmount },
    { index: 2, name: "Brazil", currency: "BRL", list: BrazilListPrice, value: BrazilDiscountValue, percent: BrazilDiscountPercent, amount: BrazilDiscountAmount, tip: BrazilTip, min: BrazilminValue, setValue: setBrazilDiscountValue, setPercent: setBrazilDiscountPercent, setAmount: setBrazilDiscountAmount },
    { index: 3, name: "Canada", currency: "CAD", list: CanadaListPrice, value: CanadaDiscountValue, percent: CanadaDiscountPercent, amount: CanadaDiscountAmount, tip: CanadaTip, min: CanadaminValue, setValue: setCanadaDiscountValue, setPercent: setCanadaDiscountPercent, setAmount: setCanadaDiscountAmount },
    { index: 4, name: "Chile", currency: "CLP", list: ChileListPrice, value: ChileDiscountValue, percent: ChileDiscountPercent, amount: ChileDiscountAmount, tip: ChileTip, min: ChileminValue, setValue: setChileDiscountValue, setPercent: setChileDiscountPercent, setAmount: setChileDiscountAmount },
    { index: 5, name: "Colombia", currency: "COP", list: ColumbiaListPrice, value: ColumbiaDiscountValue, percent: ColumbiaDiscountPercent, amount: ColumbiaDiscountAmount, tip: ColumbiaTip, min: ColumbiaminValue, setValue: setColumbiaDiscountValue, setPercent: setColumbiaDiscountPercent, setAmount: setColumbiaDiscountAmount },
    { index: 6, name: "Egypt", currency: "EGP", list: EgyptListPrice, value: EgyptDiscountValue, percent: EgyptDiscountPercent, amount: EgyptDiscountAmount, tip: EgyptTip, min: EgyptminValue, setValue: setEgyptDiscountValue, setPercent: setEgyptDiscountPercent, setAmount: setEgyptDiscountAmount },
    { index: 7, name: "European Union", currency: "EUR", list: EUListPrice, value: EUDiscountValue, percent: EUDiscountPercent, amount: EUDiscountAmount, tip: EUTip, min: EUminValue, setValue: setEUDiscountValue, setPercent: setEUDiscountPercent, setAmount: setEUDiscountAmount },
    { index: 8, name: "United Kingdom", currency: "GBP", list: GBPListPrice, value: GBPDiscountValue, percent: GBPDiscountPercent, amount: GBPDiscountAmount, tip: GBPTip, min: GBPminValue, setValue: setGBPDiscountValue, setPercent: setGBPDiscountPercent, setAmount: setGBPDiscountAmount },
    { index: 9, name: "Indonesia", currency: "IDR", list: IndonesiaListPrice, value: IndonesiaDiscountValue, percent: IndonesiaDiscountPercent, amount: IndonesiaDiscountAmount, tip: IndonesiaTip, min: IndonesiaminValue, setValue: setIndonesiaDiscountValue, setPercent: setIndonesiaDiscountPercent, setAmount: setIndonesiaDiscountAmount },
    { index: 10, name: "Israel", currency: "ILS", list: IsrealListPrice, value: IsrealDiscountValue, percent: IsrealDiscountPercent, amount: IsrealDiscountAmount, tip: IsrealTip, min: IsrealminValue, setValue: setIsrealDiscountValue, setPercent: setIsrealDiscountPercent, setAmount: setIsrealDiscountAmount },
    { index: 11, name: "India", currency: "INR", list: IndiaListPrice, value: IndiaDiscountValue, percent: IndiaDiscountPercent, amount: IndiaDiscountAmount, tip: IndiaTip, min: IndiaminValue, setValue: setIndiaDiscountValue, setPercent: setIndiaDiscountPercent, setAmount: setIndiaDiscountAmount },
    { index: 12, name: "Japan", currency: "JPY", decimals: false, list: JapanListPrice, value: JapanDiscountValue, percent: JapanDiscountPercent, amount: JapanDiscountAmount, tip: JapanTip, min: JapanminValue, setValue: setJapanDiscountValue, setPercent: setJapanDiscountPercent, setAmount: setJapanDiscountAmount },
    { index: 13, name: "South Korea", currency: "KRW", list: SKListPrice, value: SKDiscountValue, percent: SKDiscountPercent, amount: SKDiscountAmount, tip: SKTip, min: SKminValue, setValue: setSKDiscountValue, setPercent: setSKDiscountPercent, setAmount: setSKDiscountAmount },
    { index: 14, name: "Mexico", currency: "MXN", list: MexicoListPrice, value: MexicoDiscountValue, percent: MexicoDicountPercent, amount: MexicoDisountAmount, tip: MexicoTip, min: MexicominValue, setValue: setMexicoDiscountValue, setPercent: setMexicoDicountPercent, setAmount: setMexicoDisountAmount },
    { index: 15, name: "Malaysia", currency: "MYR", list: MalaysiaListPrice, value: MalaysiaDiscountValue, percent: MalaysiaDiscountPercent, amount: MalaysiaDiscountAmount, tip: MalaysiaTip, min: MalaysiaminValue, setValue: setMalaysiaDiscountValue, setPercent: setMalaysiaDiscountPercent, setAmount: setMalaysiaDiscountAmount },
    { index: 16, name: "Nigeria", currency: "NGN", list: NigeriaListPrice, value: NigeriaDiscountValue, percent: NigeriaDiscountPercent, amount: NigeriaDiscountAmount, tip: NigeriaTip, min: NigeriaminValue, setValue: setNigeriaDiscountValue, setPercent: setNigeriaDiscountPercent, setAmount: setNigeriaDiscountAmount },
    { index: 17, name: "Norway", currency: "NOK", list: NorwayListPrice, value: NorwayDiscountValue, percent: NorwayDiscountPercent, amount: NorwayDiscountAmount, tip: NorwayTip, min: NorwayminValue, setValue: setNorwayDiscountValue, setPercent: setNorwayDiscountPercent, setAmount: setNorwayDiscountAmount },
    { index: 18, name: "Peru", currency: "PEN", list: PeruListPrice, value: PeruDiscountValue, percent: PeruDiscountPercent, amount: PeruDiscountAmount, tip: PeruTip, min: Peruminvalue, setValue: setPeruDiscountValue, setPercent: setPeruDiscountPercent, setAmount: setPeruDiscountAmount },
    { index: 19, name: "Philippines", currency: "PHP", list: PhilipinesListPrice, value: PhiliphinesDiscountValue, percent: PhilipinesDiscountPercent, amount: PhilipinesAmount, tip: PhilipinesTip, min: PhilipinesminValue, setValue: setPhiliphinesDiscountValue, setPercent: setPhilipinesDiscountPercent, setAmount: setPhilipinesAmount },
    { index: 20, name: "Poland", currency: "PLN", list: PolandListPrice, value: PolandDiscountValue, percent: PolandDiscountPercent, amount: PolandDiscountAmount, tip: PolandTip, min: PolandminValue, setValue: setPolandDiscountValue, setPercent: setPolandDiscountPercent, setAmount: setPolandDiscountAmount },
    { index: 21, name: "Romania", currency: "RON", list: RomaniaListPrice, value: RomaniaDiscountValue, percent: RomaniaDiscountPercent, amount: RomaniaDiscountAmount, tip: RomaniaTip, min: Romaniaminvalue, setValue: setRomaniaDiscountValue, setPercent: setRomaniaDiscountPercent, setAmount: setRomaniaDiscountAmount },
    { index: 22, name: "Russia", currency: "RUB", list: RussiaListPrice, value: RussiaDiscountValue, percent: RussiaDiscountPercent, amount: RussiaDiscountAmount, tip: RussiaTip, min: RussiaminValue, setValue: setRussiaDiscountValue, setPercent: setRussiaDiscountPercent, setAmount: setRussiaDiscountAmount },
    { index: 23, name: "Singapore", currency: "SGD", list: SingaporeListPrice, value: SingaporeDiscountValue, percent: SingaporeDiscountPercent, amount: SingaporeDiscountAmount, tip: SingaporeTip, min: SingaporeminValue, setValue: setSingaporeDiscountValue, setPercent: setSingaporeDiscountPercent, setAmount: setSingaporeDiscountAmount },
    { index: 24, name: "Thailand", currency: "THB", list: ThailandListPrice, value: ThailandDiscountValue, percent: ThailandDiscountPercent, amount: ThailandDiscountAmount, tip: ThailandTip, min: ThailandminValue, setValue: setThailandDiscountValue, setPercent: setThailandDiscountPercent, setAmount: setThailandDiscountAmount },
    { index: 25, name: "Turkey", currency: "TRY", inTable: false, list: TurkeyListPrice, value: TurkeyDiscountValue, percent: TurkeyDiscountPercent, amount: TurkeyDiscountAmount, tip: TurkeyTip, min: TurkeyminValue, setValue: setTurkeyDiscountValue, setPercent: setTurkeyDiscountPercent, setAmount: setTurkeyDiscountAmount },
    { index: 26, name: "Taiwan", currency: "TWD", list: TaiwanListPrice, value: TaiwanDiscountValue, percent: TaiwanDiscountPercent, amount: TaiwanDiscountAmount, tip: TaiwanTip, min: TaiwanminValue, setValue: setTaiwanDiscountValue, setPercent: setTaiwanDiscountPercent, setAmount: setTaiwanDiscountAmount },
    { index: 27, name: "Vietnam", currency: "VND", list: VietnamListPrice, value: VietnamDiscountValue, percent: VietnamDiscountPercent, amount: VietnamDisocuntAmount, tip: VietnamTip, min: VietnamminValue, setValue: setVietnamDiscountValue, setPercent: setVietnamDiscountPercent, setAmount: setVietnamDisocuntAmount },
    { index: 28, name: "South Africa", currency: "ZAR", list: SAListPrice, value: SADiscountValue, percent: SADiscountPercent, amount: SADiscountAmount, tip: SATip, min: SAminValue, setValue: setSADiscountValue, setPercent: setSADiscountPercent, setAmount: setSADiscountAmount },
  ];

  // Rows shown in the table, alphabetical
  const tableCountries = countries
    .filter(c => c.inTable !== false)
    .sort((a, b) => a.name.localeCompare(b.name));


  // ================= CREATE DISCOUNT COUPON =================
  const handleDiscountCouponCreate = () => {
    setloading_btn(true)

    const isValid = COUPON_CODE_REGEX.test(couponCodeDiscount);

    const SD = moment(startDateDiscount, 'DD-MM-YYYY').format('YYYY-MM-DD HH:mm:ss');
    const ED = moment(endDateDiscount, 'DD-MM-YYYY').format('YYYY-MM-DD HH:mm:ss');

    if (!(isValid || couponCodeDiscount === "")) {
      ErrorAlert("Error", COUPON_CODE_ERROR);
      setloading_btn(false)
      return
    }

    if (DDiscountValue === "" || toNumber(DDiscountPercent) === 0 || isNaN(toNumber(DDiscountPercent)) || toNumber(DDiscountAmount) === 0 || isNaN(toNumber(DDiscountAmount))) {
      ErrorAlert("Error", "Global Price cannot be empty")
      setloading_btn(false)
      return
    }

    const raw = {
      "code": `${couponCodeDiscount}`,
      "start_date": `${SD}`,
      "end_date": `${ED}`,
      "course_code": `${code}`,
      "global_list_price": `${orZero(DGlobalPricing)}`,
      "global_discount_price": `${orZero(DDiscountValue)}`,
      "global_discount_percentage": `${orZero(DDiscountPercent)}`,
      "global_discount": `${orZero(DDiscountAmount)}`,
      "prices": countries.map(c => ({
        "discount": `${orZero(c.percent)}`,
        "discount_amount": `${orZero(c.amount)}`,
        "discount_price": `${orZero(c.value)}`,
        "list_price": `${orZero(c.list)}`,
        "country_name": c.name
      }))
    }

    SaveDiscountDouponsAPI(code, raw, setloading_btn)
  }


  // ================= RENDER =================
  return (
    <div className='col-md-8'>
      <Card className="py-2 my-2">

        <h5 className='p-2'><b>Create new Coupon</b></h5>

        <div className='row'>

          <div className='col-md-6'>
            <Card className='border border-dark border-3 h-100'>
              <Radio
                checked={selectedValue === 'free-coupon'}
                onChange={handleChange}
                value="free-coupon"
                name="radio-buttons"
                inputProps={{ 'aria-label': 'A' }}
              />
              <span><b>Free Coupon</b></span>

              <div className='container'>
                <p>Creates a limited-time offer that allows up to 1000 students to enroll in your course for free.</p>
                <p>Expiry: 1000 redemptions or 7 days after activation, whichever comes first.</p>
              </div>
            </Card>
          </div>

          <div className='col-md-6'>
            <Card className='border border-dark border-3 h-100'>
              <Radio
                checked={selectedValue === 'discount-coupon'}
                onChange={handleChange}
                value="discount-coupon"
                name="radio-buttons"
                inputProps={{ 'aria-label': 'B' }}
              />
              <span><b>Discount Coupon</b></span>

              <div className='container'>
                <p>Creates a 30 day offer for a price you decide on with unlimited redemptions.</p>
                <p>Expiry: 30 days after activation</p>
              </div>
            </Card>
          </div>

        </div>


        {/* ================= FREE COUPON ================= */}
        {selectedValue === "free-coupon" && (
          <div className='container'>
            <div className='row my-5'>
              <div className='mb-3'>
                <h6><b>Start date (DD-MM-YYYY):</b></h6>
                <DatePicker
                  format="DD-MM-YYYY"
                  size="large"
                  value={startDate}
                  // onChange={handleStartDateChange}
                  disabled
                />
              </div>

              <div className='mb-3'>
                <h6><b>End date (DD-MM-YYYY):</b></h6>
                <DatePicker
                  format="DD-MM-YYYY"
                  size="large"
                  value={endDate}
                  disabled
                />
              </div>
            </div>

            <Form.Group className='d-flex' controlId="freeCouponCode">
              <Form.Label><b>Enter coupon code (optional)</b></Form.Label>
              <Form.Control
                value={couponCodeFree}
                onChange={(e) => setcouponCodeFree(e.target.value.toUpperCase())}
                type="text"
                placeholder="Enter Coupon"
              />
            </Form.Group>

            <p>
              The coupon code must be between 6 - 20 characters, only UPPERCASE LETTERS (A-Z), numbers (0-9) and these symbols can be used: periods (.), dashes (-), and underscores (_). Coupon codes with lowercase or other symbols cannot be created. A coupon code can only be used once per course.
            </p>

            <div className='my-2'>
              {loading_btn
                ? <Button variant='contained' disabled>Loading...</Button>
                : <Button onClick={handleFreeCouponCreate} variant='contained'>Create Coupon</Button>
              }
            </div>
          </div>
        )}


        {/* ================= DISCOUNT COUPON ================= */}
        {selectedValue === "discount-coupon" && (
          <div className='container'>

            <div className='row my-5'>
              <div className='mb-3'>
                <h6><b>Start date (DD-MM-YYYY):</b></h6>
                <DatePicker
                  format="DD-MM-YYYY"
                  size="large"
                  value={moment(startDateDiscount, 'DD-MM-YYYY')}
                  // onChange={handleStartDateChangeDiscount}
                  disabled
                />
              </div>

              <div className='mb-3'>
                <h6><b>End date (DD-MM-YYYY):</b></h6>
                <DatePicker
                  format="DD-MM-YYYY"
                  size="large"
                  value={moment(endDateDiscount, 'DD-MM-YYYY')}
                  disabled
                />
              </div>
            </div>

            <Form.Group className='d-flex' controlId="discountCouponCode">
              <Form.Label><b>Enter coupon code (Optional):</b></Form.Label>
              <Form.Control
                value={couponCodeDiscount}
                onChange={(e) => setcouponCodeDiscount(e.target.value.toUpperCase())}
                type="text"
                placeholder="Enter Coupon"
              />
            </Form.Group>

            <p>The coupon code must be between 6 - 20 characters, only UPPERCASE LETTERS (A-Z), numbers (0-9) and these symbols can be used: periods (.), dashes (-), and underscores (_). Coupon codes with lowercase or other symbols cannot be created. A coupon code can only be used once per course.</p>

            <div className="pricing-container my-3">
              <div className="price-range-container p-3">
                <p><b>Enter discount price(s) for coupon:</b></p>

                <p>You have options to either:</p>
                <ol>
                  <li>Only enter a Global discount price which will be applicable to all countries.</li>
                  <p className="my-2">OR</p>
                  <li>Enter specific discount prices for countries. The Global discount price will be applied for countries
                    not in the country list below. The Global discount price will also be applied for any country for
                    which you do not enter a discount price.
                  </li>
                </ol>


                {/* ---------- Global ---------- */}
                <div className="row border border-dark p-2">
                  <p className='my-3'><b>Enter Global Discount Price (required):</b></p>

                  <div className="price-range col-md-3">
                    <Form.Label className="pricing-label"><b>Global List Price (USD)</b></Form.Label>
                    <InputGroup>
                      <InputGroup.Text id="basic-addon1">$</InputGroup.Text>
                      <Form.Control
                        value={formatMoney(DGlobalPricing)}
                        disabled readOnly
                        placeholder="USD"
                        aria-label="USD"
                        aria-describedby="basic-addon1"
                      />
                    </InputGroup>
                    <Form.Label style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                      <i>Price range: ${formatMoney(PriceRangeMinDefault)} – ${formatMoney(PriceRangeMaxDefault)}</i>
                    </Form.Label>
                  </div>

                  <div className="col-md-3">
                    <Form.Label className="pricing-label"><b>Discount Amount (USD)</b></Form.Label>
                    <Form.Control disabled readOnly value={DDiscountAmount} type="text" />
                    <Form.Label style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                      <i>Tip: Pricing around ${formatMoney(DTip)} may optimise sales</i>
                    </Form.Label>
                  </div>

                  <div className="col-md-2">
                    <Form.Label className="pricing-label"><b>Discount %</b></Form.Label>
                    <Form.Control disabled readOnly value={DDiscountPercent} type="text" />
                  </div>

                  <div className="col-md-3">
                    <Form.Label className="pricing-label"><b>Global Discounted Price (USD)</b></Form.Label>
                    <Form.Control
                      disabled={isNaN(toNumber(DGlobalPricing)) || toNumber(DGlobalPricing) <= 0}
                      isInvalid={toNumber(DDiscountValue) > 0 && toNumber(DDiscountValue) < toNumber(MinDefaultValue)}
                      onChange={handleChangeGlobalPrice}
                      value={DDiscountValue}
                      type="text"
                      inputMode="decimal"
                    />
                    <Form.Control.Feedback type="invalid">
                      Not within range
                    </Form.Control.Feedback>
                    <Form.Label style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                      <i>Minimum: ${formatMoney(MinDefaultValue)}</i>
                    </Form.Label>
                  </div>
                </div>
              </div>


              {/* ---------- Country table ---------- */}
              <p className='my-3'><b>Enter Country Specific Discount Prices (optional):</b></p>
              <table className="table table-striped text-center">
                <thead>
                  <tr>
                    <th className="col-2" scope="col">Country</th>
                    <th scope="col">Currency</th>
                    <th scope="col">Price Range</th>
                    <th scope="col">List Price</th>
                    <th scope="col">Discount Amount</th>
                    <th scope="col">Discount %</th>
                    <th scope="col">Discounted Price</th>
                  </tr>
                </thead>

                <tbody>
                  {tableCountries.map((c) => {
                    const decimals = c.decimals !== false;
                    const symbol = getSymbolFromCurrency(c.currency);
                    const range = countriesData[c.index];
                    const entered = toNumber(c.value);
                    const invalid = entered > 0 && entered < toNumber(c.min);
                    const isListZero = isNaN(toNumber(c.list)) || toNumber(c.list) <= 0;

                    return (
                      <tr key={c.name}>
                        <td>
                          {c.name}
                          <div className="mt-3">
                            <Form.Label className="tit fst-italic">
                              Tip: Pricing around {symbol}{formatMoney(c.tip, decimals)} may maximise sales.
                            </Form.Label>
                          </div>
                        </td>

                        <td>{c.currency}</td>

                        <td style={{ whiteSpace: 'nowrap' }}>
                          {range != null
                            ? `${symbol} ${formatMoney(range.minPrice, decimals)} - ${symbol} ${formatMoney(range.maxPrice, decimals)}`
                            : ''}
                        </td>

                        <td>
                          <Form.Control disabled readOnly value={formatMoney(c.list, decimals)} type="text" />
                        </td>

                        <td>
                          <Form.Control disabled readOnly value={c.amount} type="text" />
                        </td>

                        <td>
                          <Form.Control disabled readOnly value={c.percent} type="text" />
                        </td>

                        <td>
                          <Form.Control
                            disabled={isListZero}
                            isInvalid={invalid}
                            value={c.value}
                            onChange={makePriceHandler(c.list, c.setValue, c.setPercent, c.setAmount, decimals)}
                            type="text"
                            inputMode={decimals ? "decimal" : "numeric"}
                          />
                          <Form.Control.Feedback type="invalid">
                            Not within range
                          </Form.Control.Feedback>
                          <Form.Label style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                            Minimum: {symbol} {formatMoney(c.min, decimals)}
                          </Form.Label>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* pricing rules */}
              <div className="p-3">
                <p>* Other EUR countries: Bulgaria, Czech Republic, Hungary, Poland, Romania, Sweden, Andorra, Monaco, Montenegro, San Marino, Vatican City, Kosovo.</p>
                <p>** Other USD countries: Puerto Rico, Guam, America Samoa, U.S. Virgin Islands, Northern Marina Islands, Ecuador, El Salvador, Zimbabwe, The British Virgin Islands, The Turks and Caicos, Timor and Leste, Bonaire, Micronesia, Palau, Marshall Islands.</p>
              </div>
            </div>

            <div className='my-2'>
              {loading_btn
                ? <Button variant='contained' disabled>Loading...</Button>
                : <Button onClick={handleDiscountCouponCreate} variant='contained'>Create Coupon</Button>
              }
            </div>

          </div>
        )}

      </Card>
    </div>
  )
}

export default AddCoupon